<template>
  <div>
    <PageListTemplate :title="title" :subtitle="subtitle" :add="add">
      <template #default>
        <DataTableTemplate
          class="mt-4"
          no-data-text="Nenhum cadastro encontrado"
          :items="items"
          :height="tableHeight"
          :loading="isLoading"
          :headers="headers"
        >
          <template v-slot:item.nome="{ item }">
            <div class="text-ellipsis">{{ item.name }}</div>
          </template>

          <template v-slot:item.created_at="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-inline secondary--text">
                  {{ formatCreatedAt(item.created_at) }}
                </div>
              </template>

              <span>{{ formatCreatedAt(item.created_at, true) }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.cpf_cnpj="{ item }">
            {{ item.cpf_cnpj ? maskCpfCnpj(item.cpf_cnpj) : "" }}
          </template>

          <template v-slot:item.phone="{ item }">
            {{ item.phone ? maskPhone(item.phone) : "" }}
          </template>

          <template v-slot:item.type="{ item }">
            <v-chip
              v-if="item.is_owner"
              class="chip-control"
              :class="{ 'mr-1': item.is_captivator }"
              :style="colorByType('owner')"
            >
              Proprietário
            </v-chip>

            <v-chip
              v-if="item.is_captivator"
              class="chip-control"
              :style="colorByType('captivator')"
            >
              Captador
            </v-chip>

            <v-chip
              v-if="!item.is_owner && !item.is_captivator"
              class="chip-control"
              :style="colorByType('contact')"
            >
              Contato
            </v-chip>
          </template>

          <template v-slot:item.acoes="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  :to="`/cadastro/${item.id}`"
                  color="#222"
                  depressed
                  small
                  icon
                >
                  <v-icon size="16"> mdi-pencil-outline </v-icon>
                </v-btn>
              </template>

              <span>Editar</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="$can('remove_fazenda')"
                  v-bind="attrs"
                  v-on="on"
                  @click="toggleDelete(item)"
                  class="ml-2"
                  color="error"
                  depressed
                  small
                  icon
                >
                  <v-icon size="20"> mdi-delete-outline </v-icon>
                </v-btn>
              </template>

              <span>Excluir</span>
            </v-tooltip>
          </template>
        </DataTableTemplate>
      </template>
    </PageListTemplate>

    <v-dialog
      v-if="item?.id && dialogDelete"
      v-model="dialogDelete"
      max-width="500"
      persistent
    >
      <DeleteItemDialog
        @confirm="onDelete"
        @close="toggleDelete"
        title="Apagar cadastro"
        :name="item.nome"
      />
    </v-dialog>
  </div>
</template>

<script>
import PageListTemplate from "@/components/templates/PageList.vue";
import DataTableTemplate from "@/components/templates/DataTable.vue";
import DeleteItemDialog from "@/components/dialogs/DeleteItem.vue";
import maskCpfCnpj from "@/utils/maskCpfCnpj";
import maskPhone from "@/utils/maskPhone";
import formatCreatedAt from "@/utils/formatCreatedAt";
import { mapActions } from "vuex";

export default {
  components: { PageListTemplate, DeleteItemDialog, DataTableTemplate },
  data: () => ({
    title: "Cadastro",
    subtitle: "Cadastro de proprietários, captadores e contatos",
    page: 1,
    isLoading: true,
    items: [],
    headers: [
      {
        text: "Código",
        align: "start",
        value: "id",
        width: 70,
      },
      {
        text: "Nome",
        align: "start",
        value: "nome",
        width: 300,
      },
      {
        text: "CPF/CNPJ",
        align: "start",
        value: "cpf_cnpj",
        width: 150,
      },
      {
        text: "Telefone",
        align: "start",
        value: "phone",
        width: 150,
      },
      {
        text: "Tipo",
        align: "start",
        value: "type",
        width: 150,
      },
      {
        text: "Registro",
        align: "start",
        value: "created_at",
        width: 150,
      },
      {
        text: "Ações",
        align: "center",
        sortable: false,
        value: "acoes",
        width: 100,
      },
    ],
    item: null,
    dialogDelete: false,
    isLoadingDelete: false,
  }),
  computed: {
    tableHeight() {
      const name = this.$vuetify.breakpoint.name;

      const offset = name === "sm" ? 235 : name === "xs" ? 210 : 244;

      return `calc(100dvh - ${offset}px)`;
    },
    add() {
      if (!this.$can("add_fazenda")) return;

      return {
        link: "/cadastro/adicionar",
        text: "Novo Cadastro",
      };
    },
  },
  methods: {
    ...mapActions("peoples", ["listPeoples", "destroyPeople"]),
    async onLoad(page = 1, itemsPerPage = 10000) {
      this.page = page;
      this.isLoading = true;

      let filters = {
        page: page,
        itemsPerPage,
      };

      const res = await this.listPeoples(filters);

      this.items = res.data;
      this.isLoading = false;
    },
    async onDelete() {
      this.isLoadingDelete = true;

      const r = await this.destroyPeople(this.item.id);

      if (r) {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-account-remove",
          message: "Cadastro excluído com sucesso.",
          type: "success",
        });

        this.item = null;
        this.dialogDelete = false;
        this.onLoad();
      } else {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-account-alert",
          message: "Não foi possível excluir o cadastro. Tente novamente.",
          type: "error",
        });
      }

      this.isLoadingDelete = false;
    },
    toggleDelete(item) {
      if (this.dialogDelete) {
        this.dialogDelete = false;
        this.item = null;
      } else {
        this.item = item;
        this.dialogDelete = true;
      }
    },
    colorByType(type) {
      const types = {
        owner: {
          backgroundColor: "#e7eefe",
          color: "#155dfc",
        },
        captivator: {
          backgroundColor: "#d0fae5",
          color: "#007a55",
        },
        contact: {
          backgroundColor: "#fef3c6",
          color: "#bb4d00",
        },
      };

      return types[type] || null;
    },
    maskCpfCnpj,
    maskPhone,
    formatCreatedAt,
  },
  async mounted() {
    this.onLoad();
  },
};
</script>
